import React from "react"
import { useSelector } from "react-redux"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"

// Documentation des permissions par groupe. Clé = nom du groupe Django (tel que
// renvoyé dans masterReducer.groups). Deux natures d'items par groupe :
// - « Accès aux modules » : MIROIR MANUEL de la matrice serveur
//   (backend/svp/access.py, alignée Excel « Droits SVPapp_V3 », 10.08.2026).
//   ⚠️ RÈGLE (décision Jordan 10.08.2026) : cette partie se maintient À LA
//   MAIN — toute évolution de MODULES/MODULES_DERIVES doit être répercutée
//   ici dans le même chantier. Pas de génération automatique (choix assumé,
//   le rendu éditorialisé prime).
// - Comportements fins (boutons, colonnes, menus) : source = audit des usages
//   de groupes dans le code. À tenir à jour si le gating évolue.
//
// ⚠️ Les NOTIFICATIONS ne sont volontairement PAS documentées ici : depuis le
// 30.07.2026 leur éligibilité est configurable (onglet Notifications), donc
// toute liste figée deviendrait fausse au premier changement. L'onglet lui-même
// est la documentation.
const GROUPES_DOC = {
	boss: {
		label: "Boss (super-admin)",
		tagline: "Accès total — contourne tous les contrôles de droits.",
		sections: [
			{
				titre: "Réservé boss",
				items: [
					"Onglets de configuration : Identité, Véhicules, Paramètres dépannage, E-mails, Notifications, attribution aux groupes",
					"Statistiques financières ; pages /erreurs et /patchnotes",
					"Case Boss (fiche dépannage) ; colonne Commission ; tarification de l'entreprise (main d'œuvre, rabais, tarifs dépannage, frais hors heures)",
				],
			},
			{
				titre: "Atelier",
				items: [
					"Édite une fiche même clôturée ; force l'ouverture d'une fiche verrouillée",
					"Seul à modifier et à rouvrir une fiche close (atelier, dépannage, location)",
					"Boutons Facturer et Clore ; colonne Payé sur les lignes",
					"Recherche : filtre Boss, boutons Marge et Totaux, suppression de fiche",
					"Toutes les vues du tableau de bord atelier",
				],
			},
			{
				titre: "Location",
				items: [
					"Corrige « à facturer » d'une location close, et carburant / dégâts après la restitution",
					"Supprime une location",
				],
			},
			{
				titre: "Dépannage",
				items: [
					"Accès aux impayés",
					"Filtres Sur place / Boss, totaux, suppression",
				],
			},
			{
				titre: "Divers",
				items: [
					"Supprime un client ou un plan d'entretien ; case « Erreur d'un employé » d'un entretien",
					"Coche et envoie les tâches, ajoute une tâche ponctuelle",
				],
			},
		],
	},
	admin: {
		label: "Admin",
		sections: [
			{
				titre: "Fiche close",
				items: [
					"Fiche close (seul le boss la modifie ou la rouvre) : Commentaires, Sous-traitance et E-mails de l'atelier, Commentaires et E-mails du dépannage, Observations et E-mails de la location restent modifiables",
				],
			},
			{
				titre: "Accès aux modules",
				items: [
					"Stats : Interventions, Mécanique, Location",
					"Configuration : Entités, Marques/modèles, Tâches, Moyens de paiement, Adresses, RH (hors attribution aux groupes)",
					"Encaissements ET facturation ; agenda ; clients ; dispatch",
					"Atelier (créer/rechercher) + modèles ; inventaire COMPLET (physique + virtuel, transfert et corrections de stock)",
					"Tâches ; location ; marges/prix d'achat sur les fiches",
				],
			},
			{
				titre: "Clients",
				items: ["Cases « sur facture » et « mauvais payeur »"],
			},
			{
				titre: "Facturation / Paiements",
				items: [
					"Colonne Payé, bouton Clore sur une fiche atelier (pas sur une fiche d'entretien : réservé à l'atelier)",
					"Bouton Facturer sur les 3 fiches (dépose les factures ; Clos si tout est en Facture et la règle de clôture remplie, sinon En attente de paiement)",
					"Passe en Clos une fiche dépannage ou location (menu des états, règle de clôture) ; seul le boss la rouvre",
					"Date et détails de paiement, upload de la facture",
					"Transactions : tous les droits sauf sur une fiche close — modifie, corrige la date et supprime aussi une ligne encaissée, envoyée à WinBIZ ou rapprochée (notes de suivi)",
					"Menus du tableau de bord : Contrôle avant facturation, Ok pour facturation, En attente de paiement, Clos",
				],
			},
			{
				titre: "Atelier",
				items: [
					"Force l'ouverture d'une fiche verrouillée",
					"Colonne prix d'achat sur les lignes, case fourniture payée",
				],
			},
			{
				titre: "Divers",
				items: [
					"Accès aux impayés dépannage",
					"Barre Atelier / Secteurs des statistiques ; carte Inventaire sur l'accueil",
				],
			},
		],
	},
	responsable_atelier: {
		label: "Responsable atelier",
		sections: [
			{
				titre: "Fiche close",
				items: [
					"Fiche close (seul le boss la modifie ou la rouvre) : Commentaires, Sous-traitance et E-mails de l'atelier, Observations et E-mails de la location restent modifiables",
				],
			},
			{
				titre: "Accès aux modules",
				items: [
					"Stats : Mécanique, Location",
					"Configuration : Entités, Marques/modèles, Tâches",
					"Agenda ; clients ; dispatch",
					"Atelier (créer/rechercher) + modèles ; inventaire COMPLET (physique + virtuel, transfert et corrections de stock) + onglet Utilisations",
					"Entretiens ; tâches ; location ; marges/prix d'achat sur les fiches",
				],
			},
			{
				titre: "Atelier",
				items: [
					"Case « Validé » sur les lignes",
					"Menus du tableau de bord : Devis à faire, Devis, Devis en attente, Travaux à commencer, Travaux terminés, À restituer, Contrôle avant facturation",
					"Fiche d'entretien : bouton Clore (réservé à l'atelier, l'administration ne clôt pas une fiche d'entretien)",
					"Fiche d'entretien : l'œil de la ligne Véhicule modifie le véhicule de la flotte (parties, détails techniques, documents) — ni création, ni suppression, ni sortie du parc",
				],
			},
			{
				titre: "Transactions (lignes sans date)",
				items: [
					"Fiches atelier et locations, à tout état sauf Clos : sur une ligne SANS date, même quittancée ou facturée, modifie tout (montant, moyen, payeur, caissier, détails, retrait d'un document) et la supprime ; une ligne datée est figée (boss ou admin)",
					"Ligne datée, envoyée à WinBIZ ou rapprochée : boss ou admin ; fiche close : boss seul",
				],
			},
		],
	},
	responsable_depannage: {
		label: "Responsable dépannage",
		sections: [
			{
				titre: "Fiche close",
				items: [
					"Fiche close (seul le boss la modifie ou la rouvre) : Commentaires et E-mails du dépannage restent modifiables",
				],
			},
			{
				titre: "Accès aux modules",
				items: [
					"Stats : Interventions, Location",
					"Configuration : Entités, Marques/modèles, Tâches",
					"Agenda ; clients ; dispatch",
					"Atelier (créer/rechercher) + modèles — PAS d'inventaire",
					"Entretiens ; tâches ; location ; marges/prix d'achat sur les fiches",
					"Dépannage › État : dépannages et fiches à compléter de tous les dépanneurs",
				],
			},
			{
				titre: "Transactions (lignes sans date)",
				items: [
					"Fiches dépannage, à tout état sauf Clos : sur une ligne SANS date, même quittancée ou facturée, modifie tout (montant, moyen, payeur, caissier, détails, retrait d'un document), la supprime et annule un encaissement terrain ; une ligne datée est figée (boss ou admin)",
					"Ligne datée, envoyée à WinBIZ ou rapprochée : boss ou admin ; fiche close : boss seul",
				],
			},
		],
	},
	admin_atelier: {
		label: "Admin atelier",
		sections: [
			{
				titre: "Fiche close",
				items: [
					"Fiche close (seul le boss la modifie ou la rouvre) : Commentaires, Sous-traitance et E-mails de l'atelier, Observations et E-mails de la location restent modifiables",
				],
			},
			{
				titre: "Accès aux modules",
				items: [
					"Stats : Location",
					"Configuration : Entités, Marques/modèles, Tâches",
					"Agenda ; clients ; dispatch",
					"Atelier (créer/rechercher) + modèles ; inventaire VIRTUEL (stock + commandes virtuelles) + onglet Utilisations",
					"Entretiens ; tâches ; location ; marges/prix d'achat sur les fiches",
				],
			},
			{
				titre: "Atelier",
				items: [
					"Voit la carte « Heures de travail » de la fiche",
					"Fiche d'entretien : bouton Clore (réservé à l'atelier, l'administration ne clôt pas une fiche d'entretien)",
					"Fiche d'entretien : l'œil de la ligne Véhicule modifie le véhicule de la flotte (parties, détails techniques, documents) — ni création, ni suppression, ni sortie du parc",
				],
			},
			{
				titre: "Transactions (lignes sans date)",
				items: [
					"Fiches atelier et locations, à tout état sauf Clos : sur une ligne SANS date, même quittancée ou facturée, modifie tout (montant, moyen, payeur, caissier, détails, retrait d'un document) et la supprime ; une ligne datée est figée (boss ou admin)",
					"Ligne datée, envoyée à WinBIZ ou rapprochée : boss ou admin ; fiche close : boss seul",
				],
			},
		],
	},
	admin_depannage: {
		label: "Admin dépannage",
		sections: [
			{
				titre: "Fiche close",
				items: [
					"Fiche close (seul le boss la modifie ou la rouvre) : Commentaires et E-mails du dépannage restent modifiables",
				],
			},
			{
				titre: "Accès aux modules",
				items: [
					"Stats : Mécanique, Location",
					"Configuration : Entités, Marques/modèles, Tâches",
					"Agenda ; clients ; dispatch",
					"Atelier (créer/rechercher) + modèles ; inventaire VIRTUEL uniquement (stock + commandes virtuelles)",
					"Tâches ; location ; marges/prix d'achat sur les fiches — PAS d'entretiens",
					"Dépannage › État : dépannages et fiches à compléter de tous les dépanneurs",
				],
			},
			{
				titre: "Transactions (lignes sans date)",
				items: [
					"Fiches dépannage, à tout état sauf Clos : sur une ligne SANS date, même quittancée ou facturée, modifie tout (montant, moyen, payeur, caissier, détails, retrait d'un document), la supprime et annule un encaissement terrain ; une ligne datée est figée (boss ou admin)",
					"Ligne datée, envoyée à WinBIZ ou rapprochée : boss ou admin ; fiche close : boss seul",
				],
			},
		],
	},
	assistant_admin: {
		label: "Assistant admin",
		sections: [
			{
				titre: "Accès aux modules",
				items: [
					"Facturation (PAS les encaissements) ; clients",
					"Case « mauvais payeur » des clients",
				],
			},
			{
				titre: "Facturation",
				items: [
					"Bouton Clore sur une fiche atelier (pas sur une fiche d'entretien : réservé à l'atelier)",
					"Bouton Facturer sur les 3 fiches (dépose les factures ; Clos si tout est en Facture et la règle de clôture remplie, sinon En attente de paiement)",
					"Passe en Clos une fiche dépannage ou location (menu des états, règle de clôture) ; seul le boss la rouvre",
					"Dater une transaction sur les fiches (pas la page Encaissements ni le Rapprochement)",
					"Menus du tableau de bord : Contrôle avant facturation, Ok pour facturation, En attente de paiement, Clos",
				],
			},
		],
	},
	employe: {
		label: "Employés",
		sections: [
			{
				titre: "Accès aux modules",
				items: [
					"Atelier : créer / rechercher, menu « Mes travaux »",
					"Location",
					"Dépannage › État : ses propres dépannages et fiches à compléter",
					"PAS d'inventaire ni de modèles (le bouton « Appliquer un modèle » est masqué)",
				],
			},
		],
	},
}

// Ordre d'affichage = ordre des colonnes de la matrice de droits (Excel
// « Droits SVPapp ») ; les groupes hors liste suivent, triés alpha.
const ORDER = [
	"boss",
	"admin",
	"responsable_atelier",
	"responsable_depannage",
	"admin_atelier",
	"admin_depannage",
	"assistant_admin",
	"employe",
]

const styles = {
	intro: {
		fontSize: 13,
		color: COLORS.textMuted,
		marginBottom: 18,
		lineHeight: 1.5,
	},
	grid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
		gap: 16,
		alignItems: "start",
	},
	title: {
		fontSize: 15,
		fontWeight: 600,
		color: COLORS.text,
	},
	key: {
		fontSize: 11,
		fontWeight: 500,
		color: COLORS.textLight,
		fontFamily: "monospace",
		marginLeft: 8,
	},
	tagline: {
		fontSize: 12.5,
		color: COLORS.textMuted,
		marginTop: 6,
		lineHeight: 1.4,
	},
	sectionTitle: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		marginTop: 16,
		marginBottom: 8,
	},
	ul: { margin: 0, paddingLeft: 18 },
	li: {
		fontSize: 13,
		color: COLORS.text,
		lineHeight: 1.5,
		marginBottom: 4,
	},
	undoc: { fontSize: 13, color: COLORS.textLight, marginTop: 12 },
}

const GroupeCard = ({ name }) => {
	const doc = GROUPES_DOC[name]
	return (
		<div style={{ ...gs.card, height: "100%" }}>
			<div style={{ display: "flex", alignItems: "baseline" }}>
				<span style={styles.title}>{doc ? doc.label : name}</span>
				{doc && doc.label !== name && (
					<span style={styles.key}>{name}</span>
				)}
			</div>

			{doc && doc.tagline && (
				<div style={styles.tagline}>{doc.tagline}</div>
			)}

			{!doc && (
				<div style={styles.undoc}>Permissions non documentées.</div>
			)}

			{doc &&
				doc.sections.map((sec) => (
					<div key={sec.titre}>
						<div style={styles.sectionTitle}>{sec.titre}</div>
						<ul style={styles.ul}>
							{sec.items.map((it, i) => (
								<li key={i} style={styles.li}>
									{it}
								</li>
							))}
						</ul>
					</div>
				))}
		</div>
	)
}

// Onglet « Groupes » de la RH : documente, par groupe puis par fonctionnalité,
// les permissions conférées. La liste des groupes vient du live
// (masterReducer.groups) ; un groupe sans documentation est signalé.
const GroupesRH = () => {
	const groups = useSelector((s) => s.masterReducer.groups) || []

	const ordered = [...groups].sort((a, b) => {
		const ia = ORDER.indexOf(a)
		const ib = ORDER.indexOf(b)
		if (ia !== -1 && ib !== -1) return ia - ib
		if (ia !== -1) return -1
		if (ib !== -1) return 1
		return a.localeCompare(b, "fr")
	})

	return (
		<>
			<div style={styles.intro}>
				Permissions conférées par chaque groupe, par fonctionnalité
				(comportement actuel de l'application). Un employé cumule les
				permissions de tous ses groupes.
			</div>
			{ordered.length === 0 ? (
				<div style={styles.undoc}>Aucun groupe.</div>
			) : (
				<div style={styles.grid}>
					{ordered.map((name) => (
						<GroupeCard key={name} name={name} />
					))}
				</div>
			)}
		</>
	)
}

export default GroupesRH
